/**
 * ac-payload.ts — normaliza el payload crudo de un Acta de Conformidad (AC) a la
 * forma tipada AcPayload, rellenando con valores vacíos las secciones ausentes.
 * Espeja a is-payload.ts.
 *
 * A diferencia del IS, el AC NO prellena nada: actividades y conclusiones
 * arrancan vacías (sin herencia ni siembra de títulos). Regla general:
 * "sembrar solo cuando la clave está ausente; respetar lo guardado aunque esté
 * vacío" — acá no hay siembra, así que ausente y [] dan ambos lista vacía.
 *
 * El editor mantiene SIEMPRE el payload completo en estado; al guardar se envía
 * entero vía updateInformePayload (no dependemos de merge en el backend).
 */
import type {
  AcFirmas,
  AcPayload,
  IeDatosGenerales,
  IeEquipo,
  IsActividad,
} from "../../types.js";
import { DEFAULT_CARGO } from "../paso-datos-generales-helpers.js";

function str(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function num(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function datosGenerales(raw: unknown, clienteFallback: string): IeDatosGenerales {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const cliente = str(o["cliente"]) || clienteFallback;
  return {
    cliente,
    atencion: str(o["atencion"]),
    supervisor: str(o["supervisor"]),
    cargo: str(o["cargo"]) || DEFAULT_CARGO,
    fechaInicio: str(o["fechaInicio"]),
    fechaCierre: str(o["fechaCierre"]),
    referencia: str(o["referencia"]),
    lugar: str(o["lugar"]),
    ocCliente: str(o["ocCliente"]),
    otMpsa: str(o["otMpsa"]),
    emision: str(o["emision"]),
    anexo: str(o["anexo"]),
    revision: num(o["revision"]),
  };
}

function equipos(raw: unknown): IeEquipo[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((e, i): IeEquipo => {
    const o = (e && typeof e === "object" ? e : {}) as Record<string, unknown>;
    const item = o["item"];
    return {
      item: typeof item === "number" && Number.isFinite(item) ? item : i + 1,
      equipo: str(o["equipo"]),
      marca: str(o["marca"]),
      modelo: str(o["modelo"]),
      serie: str(o["serie"]),
      designacion: str(o["designacion"]),
    };
  });
}

/** Actividades del AC: arrancan vacías (sin prellenar títulos como el IS). */
function actividades(raw: unknown): IsActividad[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((a): IsActividad => {
    const o = (a && typeof a === "object" ? a : {}) as Record<string, unknown>;
    const fotoUrl = str(o["fotoUrl"]);
    // fotoUrl es opcional: solo se incluye si hay valor.
    return fotoUrl
      ? { descripcion: str(o["descripcion"]), fotoUrl }
      : { descripcion: str(o["descripcion"]) };
  });
}

function conclusiones(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((c) => str(c));
}

/** Firmas: `tecnico` siempre presente (string, "" si no hay); `cliente` opcional. */
function firmas(raw: unknown): AcFirmas {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const cliente = str(o["cliente"]);
  const base: AcFirmas = { tecnico: str(o["tecnico"]) };
  // cliente se omite cuando está vacío.
  return cliente ? { ...base, cliente } : base;
}

/** Construye un AcPayload completo a partir del payload crudo del informe. */
export function toAcPayload(raw: Record<string, unknown>, clienteFallback: string): AcPayload {
  return {
    datosGenerales: datosGenerales(raw["datosGenerales"], clienteFallback),
    equipos: equipos(raw["equipos"]),
    // AC: actividades y conclusiones arrancan vacías (sin herencia ni siembra).
    actividades: actividades(raw["actividades"]),
    conclusionesRecomendaciones: conclusiones(raw["conclusionesRecomendaciones"]),
    firmas: firmas(raw["firmas"]),
  };
}
